import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, CreditCard, Warning } from "@phosphor-icons/react";

export default function QuotePay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t");
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const { data } = await api.get(`/shop/quotes/${id}/pay-info`, { params: { t, session_id: sessionId || undefined } });
        if (cancelled) return;
        setInfo(data);
        if (sessionId && !data.paid && attempt < 10) setTimeout(() => load(attempt + 1), 2000);
      } catch { if (!cancelled) setError(true); }
    };
    load();
    return () => { cancelled = true; };
  }, [id, t, sessionId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shop/quotes/${id}/pay`, { t, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-20" data-testid="quote-pay-page">
        {error && (
          <div className="text-center" data-testid="quote-pay-error">
            <Warning size={56} weight="fill" className="text-[#FF5722] mx-auto mb-4" />
            <h1 className="font-chivo font-black text-3xl text-white uppercase">Quote link not valid</h1>
            <p className="font-plex text-[#9CA3AF] mt-2">Please use the link from your quote email or contact us.</p>
          </div>
        )}
        {info && info.paid && (
          <div className="text-center" data-testid="quote-pay-paid">
            <CheckCircle size={64} weight="fill" className="text-[#00D4FF] mx-auto mb-4" />
            <h1 className="font-chivo font-black text-4xl text-white uppercase">Payment Received</h1>
            <p className="font-plex text-[#9CA3AF] mt-2">Thanks {info.name}. We'll be in touch with delivery details for your {info.product_name}.</p>
          </div>
        )}
        {info && !info.paid && (
          <div className="surface p-8" data-testid="quote-pay-details">
            <div className="section-label mb-3">— YOUR QUOTE</div>
            <h1 className="font-chivo font-black text-3xl text-white uppercase tracking-tight">{info.product_name}</h1>
            <p className="font-plex text-sm text-[#9CA3AF] mt-1">Prepared for {info.name}</p>
            {info.description && <p className="font-plex text-sm text-[#9CA3AF] mt-4 whitespace-pre-wrap" data-testid="quote-pay-description">{info.description}</p>}
            <div className="flex items-end justify-between border-t border-[#262626] mt-6 pt-4">
              <div>
                <div className="font-mono font-bold text-3xl text-[#00D4FF]" data-testid="quote-pay-amount">{formatMoney(info.amount)}</div>
                <div className="font-mono text-[10px] text-[#9CA3AF] tracking-widest uppercase">+ tax calculated at checkout</div>
              </div>
              <button onClick={pay} disabled={busy || sessionId} className="btn-primary" data-testid="quote-pay-btn">
                <CreditCard size={14} weight="bold" /> {sessionId ? "Confirming..." : busy ? "Opening..." : "Pay Securely"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
